/**
 * INIKRU — business config (A0 spine, frontend side).
 *
 * Mirrors utils/currency.js exactly: the industry + capability + vocabulary
 * config is fetched ONCE at login from GET /api/business-config/ and cached in
 * localStorage so a hard reload configures the UI immediately (before the fetch
 * returns). The whole app should ask `hasCapability('khata')` — never "which
 * industry" — and read words through `word('product')`.
 *
 * ⚠️ Backward-compat: the DEFAULT below is the fashion reference config
 * (storefront on, everything else off). Existing users resolve to exactly this
 * from the backend, so nothing they see changes. This util is intentionally NOT
 * wired into any existing screen yet — features start reading it as each
 * capability (A1…A8) ships.
 */

const STORAGE_KEY = 'inikru_business_config';

const DEFAULT = {
  industry: 'fashion_ecommerce',
  industry_label: 'Fashion / E-commerce',
  first_job: 'sell_online',
  storefront_enabled: true,
  capabilities: { storefront: true },   // everything else falls to false
  vocabulary: {
    product: 'product', products: 'products',
    store: 'Store', my_store: 'My Store',
    catalog: 'Catalog', customer: 'customer',
  },
  default_units: ['piece'],
  // Business blueprint. DEFAULT = fashion = every surface on, bespoke catalog
  // form (empty schema), today's homepage (empty widgets). hasSurface() below
  // fails OPEN, so an existing user whose cached config predates `surfaces`
  // still sees every section — nothing regresses.
  surfaces: {
    storefront_design: true, analytics: true, collections: true, coupons: true,
    marketing: true, online_orders: true, connect_payments: true,
  },
  catalog_schema: [],
  list_columns: [],
  dashboard_widgets: [],
};

const read = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...DEFAULT, ...JSON.parse(raw) };
  } catch { /* ignore malformed cache */ }
  return { ...DEFAULT };
};

let _config = read();

// Screens branch on this config (which dashboard to render, which nav items to
// show) but it is a module singleton, not React state. A plain window event
// wasn't enough: the config often arrives BEFORE a screen has mounted and
// subscribed, and that screen then keeps rendering the wrong thing until the
// next navigation. Callers therefore read `configVersion()` as state and
// re-check it when they subscribe, which closes that race.
let _version = 0;
const _listeners = new Set();

export const configVersion = () => _version;

export const subscribeBusinessConfig = (fn) => {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
};

/** Store the config fetched at login (persists for reloads). */
export const setBusinessConfig = (cfg) => {
  if (!cfg || typeof cfg !== 'object') return;
  _config = { ...DEFAULT, ...cfg };
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(_config)); } catch { /* quota */ }
  _version += 1;
  _listeners.forEach((fn) => { try { fn(_version); } catch { /* a bad listener must not break login */ } });
};

/** Clear on logout so the next user doesn't inherit this one's config. */
export const clearBusinessConfig = () => {
  _config = { ...DEFAULT };
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
};

export const getBusinessConfig = () => _config;

/** THE call features use: `if (hasCapability('khata')) { ... }`. */
export const hasCapability = (key) => Boolean(_config.capabilities?.[key]);

/**
 * TOOLS — the one question the UI should ask from here on:
 * `isToolOn('inventory')`, whether that switch happens to be stored as a
 * capability or as an installed plugin. The backend sends the merged map in
 * business-config; this is a read of that, never a second copy.
 *
 * FAIL-SOFT by design: a config cached before tools existed has no `tools` map,
 * so we fall back to the capability map — an existing user is never worse off
 * than they were, and the next login fills the map in.
 */
export const getTools = () => _config.tools || null;

/**
 * One of a tool's own options, as set on the Tools page (Tools → the tool's
 * gear). The backend already resolved defaults, so a business that never opened
 * the settings gets exactly the behaviour it had before settings existed.
 * Returns undefined if the config predates tool settings — always `|| fallback`.
 */
export const toolSetting = (tool, field) => _config.tool_settings?.[tool]?.[field];

export const isToolOn = (key) => {
  const tools = _config.tools;
  if (tools && typeof tools === 'object' && key in tools) return Boolean(tools[key]);
  return hasCapability(key);   // pre-tools cache → old behaviour
};

/** Native word for this business, with a safe fallback. */
export const word = (key, fallback) =>
  _config.vocabulary?.[key] || fallback || DEFAULT.vocabulary[key] || key;

/** Title-cased native word (for nav titles / labels). */
export const capWord = (key, fallback) => {
  const w = word(key, fallback);
  return w ? w.charAt(0).toUpperCase() + w.slice(1) : w;
};

export const getIndustry = () => _config.industry;
export const getFirstJob = () => _config.first_job;
export const getDefaultUnits = () => _config.default_units || ['piece'];

/**
 * Whether a whole UI section is present for this business. FAIL-OPEN: an unknown
 * surface, or a config with no `surfaces` map at all (old cache / pre-blueprint
 * user), returns true — so existing users never lose a section. Only an explicit
 * `false` from the backend blueprint hides it.
 */
export const hasSurface = (name) => {
  const s = _config.surfaces;
  if (!s || typeof s !== 'object') return true;
  return s[name] !== false;
};

/** The schema-driven catalog form fields (empty → use the bespoke form). */
export const getCatalogSchema = () => _config.catalog_schema || [];
export const getListColumns = () => _config.list_columns || [];
export const getDashboardWidgets = () => _config.dashboard_widgets || [];
/** True when this business has its own catalog schema (non-fashion verticals). */
export const hasCatalogSchema = () => (_config.catalog_schema || []).length > 0;

/**
 * Multi-user role of the logged-in person. 'owner' for a solo owner (and for any
 * legacy config with no role) — so nothing changes for existing single-user
 * businesses. Staff carry 'manager' / 'cashier' / 'accountant' / 'staff'.
 */
export const getRole = () => _config.role || 'owner';

/**
 * Whether the current person may access a functional area (money, settings,
 * team…). FAIL-OPEN: no role_areas (owner / legacy) → true, so owners see
 * everything. Only an explicit list that omits the area hides it.
 */
export const canAccessArea = (area) => {
  const areas = _config.role_areas;
  if (!Array.isArray(areas)) return true;   // owner / legacy → full access
  return areas.includes(area);
};

/** True only for the business owner (staff never manage staff). */
export const isOwner = () => getRole() === 'owner';

/**
 * A brand-new business that hasn't chosen its features yet is gated to the setup
 * page until it does. Only ever true right after registration; existing users
 * (whose config has no needs_setup) are never gated.
 */
export const needsSetup = () => Boolean(_config.needs_setup);

// True only for a genuinely NEW business (server stamps onboarding.show at
// registration). Existing businesses return false, so first-run onboarding
// (the "your store is LIVE" tour + Setup-Studio checklist) never re-appears.
export const isNewBusiness = () => Boolean(_config.is_new_business);

/**
 * Beta gate for the owner Tools + "Make it yours" (Personalize) surfaces.
 * FAILS CLOSED: a config with no `beta_tools` flag — the default, or any cache
 * from before this flag shipped — resolves to false, so these still-in-progress
 * pages stay hidden AND route-blocked for everyone until the backend flips
 * INIKRU_BETA_TOOLS on (one env var). Nothing else in the app reads this.
 */
export const canUseTools = () => Boolean(_config.beta_tools);

// first_job → the screen a NEW business should land on after login. Fashion's
// job (sell_online) maps to today's /homepage, so existing users are unchanged.
// Gated destinations (inventory/payments/customers) land on a graceful
// "enable this feature" screen if the plugin is off — an intentional nudge.
const FIRST_JOB_ROUTES = {
  sell_online: '/homepage',
  counter_billing: '/orderbook',
  track_stock: '/inventory',
  track_money: '/payments',
  manage_customers: '/customers',
};

/** The landing route for this business's chosen first job (defaults /homepage). */
export const firstJobRoute = () =>
  FIRST_JOB_ROUTES[_config.first_job] || '/homepage';
